import {
  Alert, Grid, LinkAsButton, Panel,
} from '@printdeal/ui-library';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { getCategoryName } from '@printdeal/business-logic';
import React, { useContext, useEffect, useState } from 'react';
import { selectionToQueryParameter } from '@printdeal/product-code';
import { Selection } from '@printdeal/product-selector';
import { ErrorManagement } from '@printdeal/error-management/react';
import { Design as DesignType, DesignInfo, ProductInfo } from '../../../../../types/design';
import { Locale } from '../../../../../types/gatsby';
import { convertNullToUndefined } from '../../../../../types/util';
import { getLanguageFromLocale } from '../../../../../helpers/translations';
import ArtWorkThumbnail from '../../ArtworkThumbnail';
import EditDesignName from './EditDesignName';
import LineItemDescription from '../../LineItemDescription';
import FileDeliveryButton from '../../../../file-delivery/FileDeliveryButton';
import useSystemPages from '../../../../../hooks/useSystemPages';
import { ServiceFactory } from '../../../../../services/ServiceFactory';
import ApiContext from '../../contexts/ApiContext';

interface Props {
  design: DesignType
  status: string
  setStatus: (status: string) => void
  updateDesign: (updatedDesign: { id: string, deleted: boolean }) => void
}

export const STATES = {
  INITIAL: 'initial',
  GET_DESIGNS: {
    LOADING: 'get-designs-loading',
    SUCCESS: 'get-designs-success',
    NO_RESULTS: 'get-designs-no-results',
    ERROR: 'get-designs-error',
  },
  DELETE_DESIGNS: {
    ERROR: 'delete-designs-error',
  },
  DUPLICATE_DESIGN: {
    ERROR: 'duplicate-design-error',
  },
};

const client = ServiceFactory.createHomePageService();

const validateJson = (designId: string, json: string) => {
  try {
    return JSON.parse(json) as ProductInfo[];
  } catch (error) {
    ErrorManagement.exception({
      exception: 'Unable to load design because of a json parse error.',
      extra: {
        designId,
        error: JSON.stringify(error),
      },
      tags: {
        fileLocation: 'designListPage',
        featureFunction: 'validateJson',
      },
      fingerprint: ['design-list-valid-json-error'],
    });
    return [];
  }
};

const Design = ({
  design, status, setStatus, updateDesign,
}: Props) => {
  const {
    id,
    designName,
    docRefList,
    productInfo,
    mcpAttributes,
    editorType,
    mcpSku,
    createdAt,
    deleted,
  } = design;
  const intl = useIntl();
  const { designsApi } = useContext(ApiContext);
  const [deletedDesignId, setDeletedDesignId] = useState<string>('');
  const [reviewPage, productsPage] = useSystemPages('review', 'productIndex');
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [product] = validateJson(id, productInfo);
  if (!product) return null;
  const {
    productPageProductId,
    custom: { fields: { userSelection, contentfulPageId } },
  } = product as ProductInfo;
  const productName = convertNullToUndefined(getCategoryName(product, getLanguageFromLocale(intl.locale)));

  const getRedirectUrl = async ({
    id: redirectDesignId,
    successUrl,
    docRefList: [{ documentReference }],
  }: DesignType | DesignInfo) => {
    if (successUrl) {
      const url = new URL(successUrl);
      url.searchParams.set('documentReferenceUrl', documentReference);
      url.searchParams.set('designId', redirectDesignId);
      url.searchParams.delete('lineItemId');
      setRedirectUrl(url.toString());
      return url.toString();
    }

    const locale = intl.locale as Locale;
    const { data: { productContent } } = await client.getReviewPage(contentfulPageId, locale);
    const reviewPath = reviewPage.path.replace(/^\/[a-z]+\//, '/'); // strip /<locale>
    const path = `${productsPage.path}/${productContent.productCategory.slug}/${productContent.slug}${reviewPath}`;
    const params = new URLSearchParams();
    const selection = selectionToQueryParameter(JSON.parse(mcpAttributes) as Selection);
    params.set('selection', decodeURIComponent(selection));
    params.set('editorType', editorType);
    params.set('documentReferenceUrl', documentReference);
    setRedirectUrl(`${window.location.origin}${path}?${params}`);
    return `${window.location.origin}${path}?${params}`;
  };

  useEffect(() => {
    getRedirectUrl(design);
  }, [docRefList[0].documentReference]);

  const deleteDesign = async (designId: string, undo: boolean = false) => {
    try {
      setDeletedDesignId(designId);
      updateDesign({ id: designId, deleted: !undo });
      designsApi?.deleteDesign(designId);
    } catch (error) {
      setStatus(STATES.DELETE_DESIGNS.ERROR);
      updateDesign({ id: designId, deleted: false });
    }
  };

  if (deleted && deletedDesignId === id) {
    return (
      <li>
        <Alert className="pd-mb-3" variant="info">
          <LinkAsButton type="button" variant="secondary" onClick={() => deleteDesign(deletedDesignId, true)}>
            <FormattedMessage
              id="account.designList.undo.delete"
              defaultMessage="Click here to Undo this delete"
            />
          </LinkAsButton>
        </Alert>
      </li>
    );
  }

  if (deleted || !designsApi) return null;

  const addToCart = async () => {
    try {
      const duplicatedDesign = await designsApi.duplicateDesign(id);
      const newDesign = await designsApi.getDesignInfo(duplicatedDesign);
      window.location.href = await getRedirectUrl(newDesign);
    } catch (error) {
      setStatus(STATES.DUPLICATE_DESIGN.ERROR);
    }
  };

  return (
    <li>
      {status === STATES.DELETE_DESIGNS.ERROR && deletedDesignId === id && (
        <Alert className="pd-mb-3" variant="error">
          <FormattedMessage
            id="account.designList.delete.error"
            defaultMessage="We were unable to delete your design."
          />
        </Alert>
      )}
      <Panel key={id}>
        <Grid className="pd-grid-cols-12 pd-gap-6">
          <Grid.Item className="pd-col-span-12 md:pd-col-span-5 lg:pd-col-span-4">
            <ArtWorkThumbnail
              documentReference={docRefList[0].documentReference}
              isDeliveredFileValid
              userSelection={// userSelection should be without Quantity
                Object.values(userSelection).filter((attribute) => !attribute.includes('Quantity'))
              }
            />
          </Grid.Item>
          <Grid.Item className="pd-col-span-12 md:pd-col-span-7 lg:pd-col-span-4">
            <EditDesignName
              initialDesignName={designName}
              updateDesign={updateDesign}
              id={id}
            />
            <time dateTime={createdAt} className="pd-mb-4 pd-mt-3 pd-block">
              <FormattedDate
                value={createdAt}
                year="numeric"
                month="long"
                day="2-digit"
              />
            </time>
            <LineItemDescription lineItem={product} productName={productName} />
          </Grid.Item>
          <Grid.Item className="pd-col-span-12 lg:pd-col-span-4">
            <LinkAsButton variant="secondary" iconBefore="shopping-cart" className="pd-text-left" onClick={() => addToCart()}>
              <FormattedMessage id="product.button.addToCart" defaultMessage="Add to cart" />
            </LinkAsButton>
            <FileDeliveryButton
              deliveryToolSettings={{ editormode: editorType }}
              selectedDeliveryTool="editor"
              mcpSKU={mcpSku}
              variantSku={product.sku}
              mcpAttributes={mcpAttributes}
              productKey={productPageProductId}
              productInfo={[product]}
              designName={designName}
              defaultDesignName={designName}
              documentReference={docRefList[0].documentReference}
              productName={productName}
              redirectUrl={redirectUrl}
              source="Account"
            >
              <LinkAsButton variant="secondary" iconBefore="edit" className="pd-text-left">
                <FormattedMessage id="account.designsList.changeDesign" defaultMessage="Open design" />
              </LinkAsButton>
            </FileDeliveryButton>
            <LinkAsButton variant="secondary" iconBefore="trashcan" className="pd-text-left" onClick={() => deleteDesign(id)}>
              <FormattedMessage id="account.designList.delete" defaultMessage="Delete" />
            </LinkAsButton>
          </Grid.Item>
        </Grid>
      </Panel>
    </li>
  );
};

export default Design;
